import React from 'react';
import { Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useLoadingStore } from '../../stores/loading';

// DownloadButton组件的Props接口
export interface DownloadButtonProps {
  /** 下载的文件名（不包含扩展名） */
  filename: string;
  /** 数据格式化函数，返回格式化后的CSV数据 */
  onGetData: () => Record<string, any>[] | Promise<Record<string, any>[]>;
  /** 按钮文本 */
  children?: React.ReactNode;
  /** 是否禁用按钮 */
  disabled?: boolean;
  /** 自定义样式类名 */
  className?: string;
  /** 点击前的回调函数，可用于数据预处理 */
  onBeforeDownload?: () => void;
  /** 下载完成后的回调函数 */
  onAfterDownload?: () => void;
  /** 自定义列标题映射 */
  columnMapping?: Record<string, string>;
}

/**
 * 将值转换为CSV友好的字符串格式
 */
const formatValueForCSV = (value: any): string => {
  if (value === null || value === undefined) {
    return '';
  }

  // 如果是对象或数组，转换为JSON字符串
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return '[Object]';
    }
  }

  // 转换为字符串
  const stringValue = String(value);

  // 处理包含逗号、引号或换行符的值
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

/**
 * 将对象数组转换为CSV格式字符串
 */
const convertToCSV = (data: Record<string, any>[], columnMapping?: Record<string, string>): string => {
  if (!data || data.length === 0) {
    return '';
  }

  // 获取所有列名
  const columns = Object.keys(data[0]);

  // 生成CSV头部（使用映射的列名或原列名）
  const headers = columns.map((col) => columnMapping?.[col] || col);
  const csvHeaders = headers.join(',');

  // 生成CSV数据行
  const csvRows = data.map((row) => {
    return columns
      .map((col) => {
        const value = row[col];
        return formatValueForCSV(value);
      })
      .join(',');
  });

  return [csvHeaders, ...csvRows].join('\n');
};

/**
 * 下载CSV文件
 */
const downloadCSV = (csvContent: string, filename: string): void => {
  // 添加BOM以支持中文字符
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

  // 创建下载链接
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // 清理URL对象
  URL.revokeObjectURL(url);
};

export default function DownloadButton({ onGetData, filename, children = 'Download', disabled = false, className, onBeforeDownload, onAfterDownload, columnMapping }: DownloadButtonProps) {
  const { setLoading } = useLoadingStore();

  const handleDownload = async () => {
    try {
      // 设置loading状态
      setLoading(true, 'Generating CSV file...');

      // 执行下载前回调
      onBeforeDownload?.();

      // 获取格式化后的数据
      const data = await onGetData();

      // 检查数据是否为空
      if (!data || data.length === 0) {
        console.warn('DownloadButton: No data available for download');
        return;
      }

      // 转换为CSV格式
      const csvContent = convertToCSV(data, columnMapping);

      // 下载文件
      downloadCSV(csvContent, filename);

      // 执行下载后回调
      onAfterDownload?.();
    } catch (error) {
      console.error('Error downloading CSV file:', error);
    } finally {
      // 清除loading状态
      setLoading(false);
    }
  };

  return (
    <StyledDownloadButton type="primary" icon={<DownloadOutlined />} disabled={disabled} onClick={handleDownload} className={className}>
      {children}
    </StyledDownloadButton>
  );
}

// 样式组件
const StyledDownloadButton = styled(Button)`
  height: 40px;
  background-color: #141413;
  border-color: #141413;
  border-radius: 28px;
  padding: 0 16px;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: rgba(16, 41, 0, 0.8) !important;
    border-color: rgba(16, 41, 0, 0.8) !important;
  }

  &:focus {
    background-color: #141413 !important;
    border-color: #141413 !important;
  }

  &:disabled {
    background-color: rgba(16, 41, 0, 0.3) !important;
    border-color: rgba(16, 41, 0, 0.3) !important;
  }
`;
